import ApplicationController from '../controllers/application_controller'
import Logger from '../lib/logger'

export default class extends ApplicationController {
  static targets = [
    "sidebar",
    "channelsList",
    "directList",
    "entitiesList",
    "messageArea",
    "modalHeader",
    "welcomeState",
    "messagesContainer",
    "contextName",
    "contextDescription",
    "contextIcon",
    "statusText",
    "searchInput",
    "channelsCount",
    "directCount",
    "entitiesCount",
    "sectionContent",
    "sectionIcon"
  ]

  static values = {
    currentUserId: Number,
    messagesFrame: { type: String, default: "messages-turbo-frame" },
    baseUrl: { type: String, default: "/messages" }
  }

  connect() {
    super.connect()

    this.currentContext = null
    this.currentContextType = null
    this.updateActiveStates()
    this.updateCounts()

    requestAnimationFrame(() => {
      this.applyMobileState()
    })

    this.setupMutationObservers()
    this.element.addEventListener('message:read', (event) => {
      this.handleMessageRead(event.detail)
    })
  }

  disconnect() {
    super.disconnect()

    if (this.observers) {
      this.observers.forEach(o => o.disconnect())
    }

    if (this.turboFrameElement && this.handleTurboFrameRender) {
      this.turboFrameElement.removeEventListener('turbo:frame-render', this.handleTurboFrameRender)
    }
  }

  /* -- Mobile state management -- */

  isShowingForm() {
    const frame = this.getMessagesFrame()
    if (!frame) return false

    const src = frame.src || ''
    const contentPaths = [
      '/channels/new', '/messages/new', '/messages/drafts',
      '/messages/threads', '/messages/search', '/entity_messages',
      '/channels', '/messages'
    ]

    try {
      const path = new URL(src, window.location.origin).pathname
      return contentPaths.some(p => path === p || path.startsWith(p + '/'))
    } catch {
      return false
    }
  }

  applyMobileState() {
    if (window.innerWidth > 768) {
      this.setupTurboFrameListener()
      return
    }

    if (!this.hasMessageAreaTarget || !this.hasSidebarTarget) {
      this.setupTurboFrameListener()
      return
    }

    if (this.currentContext || this.isShowingForm()) {
      this.sidebarTarget.classList.add('mobile-hidden')
      this.messageAreaTarget.classList.remove('mobile-hidden')
      if (this.hasModalHeaderTarget) this.modalHeaderTarget.classList.add('mobile-hidden')
    } else {
      this.messageAreaTarget.classList.add('mobile-hidden')
      this.sidebarTarget.classList.remove('mobile-hidden')
      if (this.hasModalHeaderTarget) this.modalHeaderTarget.classList.remove('mobile-hidden')
    }

    this.hideBackdrop()
    this.setupTurboFrameListener()
  }

  setupTurboFrameListener() {
    const frame = this.getMessagesFrame()
    if (!frame) return

    this.turboFrameElement = frame

    if (this.handleTurboFrameRender) {
      frame.removeEventListener('turbo:frame-render', this.handleTurboFrameRender)
    }

    this.handleTurboFrameRender = () => {
      requestAnimationFrame(() => this.applyMobileState())
    }

    frame.addEventListener('turbo:frame-render', this.handleTurboFrameRender)
  }

  setupMutationObservers() {
    this.observers = []
    const listTargets = [
      this.hasChannelsListTarget ? this.channelsListTarget : null,
      this.hasDirectListTarget ? this.directListTarget : null,
      this.hasEntitiesListTarget ? this.entitiesListTarget : null
    ].filter(Boolean)

    for (const target of listTargets) {
      const observer = new MutationObserver(() => this.updateCounts())
      observer.observe(target, { childList: true, subtree: true })
      this.observers.push(observer)
    }
  }

  /* -- Mobile animations -- */

  showMobileMessageArea() {
    if (window.innerWidth > 768 || !this.hasSidebarTarget || !this.hasMessageAreaTarget) return

    this.hideBackdrop()
    this.sidebarTarget.classList.add('mobile-slide-out')
    this.messageAreaTarget.classList.remove('mobile-hidden')
    this.messageAreaTarget.classList.add('mobile-slide-in')

    if (this.hasModalHeaderTarget) {
      this.modalHeaderTarget.classList.add('mobile-hidden')
    }

    setTimeout(() => {
      this.sidebarTarget.classList.add('mobile-hidden')
      this.sidebarTarget.classList.remove('mobile-slide-out')
      this.messageAreaTarget.classList.remove('mobile-slide-in')
    }, 350)
  }

  showMobileSidebar(event) {
    if (event) event.preventDefault()
    
    if (!this.hasSidebarTarget || !this.hasMessageAreaTarget) return

    this.hideBackdrop()
    this.messageAreaTarget.classList.add('mobile-slide-out-reverse')
    this.sidebarTarget.classList.remove('mobile-hidden')
    this.sidebarTarget.classList.add('mobile-slide-in-reverse')

    if (this.hasModalHeaderTarget) {
      this.modalHeaderTarget.classList.remove('mobile-hidden')
    }

    setTimeout(() => {
      this.messageAreaTarget.classList.add('mobile-hidden')
      this.messageAreaTarget.classList.remove('mobile-slide-out-reverse')
      this.sidebarTarget.classList.remove('mobile-slide-in-reverse')
    }, 350)

    this.clearSelection()
  }

  clearSelection() {
    this.currentContext = null
    this.currentContextType = null

    if (this.hasContextNameTarget) this.contextNameTarget.textContent = 'Messaging'
    if (this.hasStatusTextTarget) this.statusTextTarget.textContent = 'Select a channel or direct message'

    this.element.querySelectorAll('.channel-item, .direct-item, .entity-item').forEach(item =>
      item.classList.remove('active'))

    const frame = this.getMessagesFrame()
    if (frame && (frame.src.includes('/new') || frame.src.includes('drafts') || frame.src.includes('threads'))) {
      frame.src = ''
    }

    this.hideCreateForms()
  }

  /* -- Backdrop management -- */

  showBackdrop() {
    let backdrop = this.element.querySelector('.messaging-sidebar-backdrop')
    if (!backdrop) {
      backdrop = document.createElement('div')
      backdrop.className = 'messaging-sidebar-backdrop'
      backdrop.dataset.action = 'click->messaging-nav#showMobileMessageArea'
      this.element.appendChild(backdrop)
    }

    void backdrop.offsetWidth
    backdrop.classList.add('show')
  }

  hideBackdrop() {
    this.element.querySelectorAll('.messaging-sidebar-backdrop').forEach(backdrop => {
      backdrop.classList.remove('show')
      backdrop.style.visibility = 'hidden'
      backdrop.style.pointerEvents = 'none'

      setTimeout(() => {
        if (backdrop.parentNode) backdrop.remove()
      }, 300)
    })
  }

  /* -- Section visibility and counts -- */

  updateCounts() {
    this.updateSectionVisibility('channels', '.channel-item',
      this.hasChannelsCountTarget ? this.channelsCountTarget : null)
    this.updateSectionVisibility('direct', '.direct-item',
      this.hasDirectCountTarget ? this.directCountTarget : null)
    this.updateSectionVisibility('entities', '.entity-item',
      this.hasEntitiesCountTarget ? this.entitiesCountTarget : null)
  }

  updateSectionVisibility(sectionId, itemSelector, countTarget) {
    const items = this.element.querySelectorAll(itemSelector)
    const count = items.length

    if (countTarget) countTarget.textContent = count

    const header = this.element.querySelector(`.section-header[data-section-id="${sectionId}"]`)
    const content = this.element.querySelector(`.collapse-section[data-section-id="${sectionId}"]`)

    if (header && content) {
      if (count === 0) {
        header.classList.add('d-none')
        content.classList.add('d-none')
      } else {
        header.classList.remove('d-none')
        content.classList.remove('d-none')
      }
    }
  }

  /* -- Context selection -- */

  selectChannel(event) {
    event.preventDefault()

    const button = event.currentTarget
    this.setContext('channel', button.dataset.channelId, button.dataset.channelName,
      button.dataset.channelType || 'public_channel')

    this.loadMessages('channels', button.dataset.channelId)
    this.setActiveItem(button, 'channel')
  }

  selectUserFromDropdown(event) {
    event.preventDefault()

    const button = event.currentTarget
    const userId = button.dataset.userId
    const userName = button.dataset.userName
    const url = `/channels/direct?user_id=${userId}`
    const frame = this.getMessagesFrame()

    if (frame) {
      frame.src = url
    } else {
      window.Turbo.visit(url)
    }

    const newUrl = `${this.baseUrlValue}?direct_user=${userId}`
    window.history.pushState({}, '', newUrl)

    this.setContext('direct', userId, userName)
    this.setActiveItem(button, 'direct')

    if (window.innerWidth <= 768) {
      this.showMobileMessageArea()
    }
  }

  selectDirectChannel(event) {
    event.preventDefault()

    const button = event.currentTarget
    this.setContext('direct', button.dataset.channelId, button.dataset.userName)
    this.loadMessages('channels', button.dataset.channelId)
    this.setActiveItem(button, 'direct')
  }

  selectEntity(event) {
    event.preventDefault()

    const button = event.currentTarget
    this.setContext('entity', button.dataset.entityId, button.dataset.entityName,
      button.dataset.entityType)

    this.loadEntityMessages(button.dataset.entityType, button.dataset.entityId)
    this.setActiveItem(button, 'entity')
  }

  handleMessageRead(detail) {
    const { messageId: _mid, messageableId, messageableType } = detail
    const item = this.element.querySelector(`[data-${messageableType}-id="${messageableId}"]`)
    if (!item) return

    const unreadIndicator = item.querySelector('.unread-dot, .badge')
    if (unreadIndicator) unreadIndicator.classList.add('d-none')
    item.classList.remove('has-unread')
  }

  setContext(type, id, name, subtype = null) {
    this.currentContext = { type, id, name, subtype }
    this.currentContextType = type

    if (this.hasContextNameTarget) {
      this.contextNameTarget.textContent = formatContextName(type, name, subtype)
    }

    if (this.hasContextDescriptionTarget) {
      this.contextDescriptionTarget.textContent = formatContextDescription(type, subtype)
    }

    if (this.hasContextIconTarget) {
      this.contextIconTarget.innerHTML = getContextIcon(type, subtype)
    }

    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = `Connected to ${name}`
    }

    if (this.hasWelcomeStateTarget) {
      this.welcomeStateTarget.classList.add('d-none')
    }
    if (this.hasMessagesContainerTarget) {
      this.messagesContainerTarget.classList.remove('d-none')
    }

    this.showMobileMessageArea()
    this.hideCreateForms()

    this.dispatch('contextChanged', { detail: { type, id, name, subtype } })
  }

  setActiveItem(activeButton) {
    this.element.querySelectorAll('.channel-item, .direct-item, .entity-item, .activity-item')
      .forEach(item => item.classList.remove('active'))

    activeButton.classList.add('active')
  }

  /* -- Message loading -- */

  loadMessages(resourceType, resourceId) {
    const frame = this.getMessagesFrame()
    if (!frame) return

    frame.src = `/${resourceType}/${resourceId}`

    const paramName = resourceType === 'channels' ? 'channel' : resourceType.slice(0, -1)
    const newUrl = `${this.baseUrlValue}?${paramName}=${resourceId}`
    window.history.pushState({}, '', newUrl)
  }

  loadEntityMessages(entityType, entityId) {
    const frame = this.getMessagesFrame()
    if (!frame) return

    const url = `/entity_messages?entity_type=${entityType}&entity_id=${entityId}`
    frame.src = url

    const newUrl = `${this.baseUrlValue}?entity_type=${entityType}&entity_id=${entityId}`
    window.history.pushState({}, '', newUrl)
  }

  loadFrameSrc(path) {
    const frame = this.getMessagesFrame()
    if (frame) {
      frame.src = path
    } else {
      window.Turbo.visit(path)
    }

    this.pushUrl(path)
    if (window.innerWidth > 768 || path.includes('/new') || path.includes('drafts') || path.includes('threads')) {
      this.showMobileMessageArea()
    }
  }

  pushUrl(path) {
    const newUrl = path.startsWith('/') ? `${this.baseUrlValue}${path}` : path
    window.history.pushState({}, '', newUrl)
  }

  /* -- Navigation actions -- */

  showCreateChannelForm(event) {
    if (event) { event.preventDefault(); event.stopPropagation() }
    this.loadFrameSrc('/channels/new')
  }

  showCreateDirectMessageForm(event) {
    if (event) { event.preventDefault(); event.stopPropagation() }
    this.loadFrameSrc('/channels/new?type=direct')
  }

  showNewChannelModal(event) { this.showCreateChannelForm(event) }
  showNewDirectMessageModal(event) { this.showCreateDirectMessageForm(event) }

  hideCreateForms(event) {
    if (event) event.preventDefault()

    if (!this.currentContext) {
      if (this.hasWelcomeStateTarget) this.welcomeStateTarget.classList.remove('d-none')
    } else if (this.hasMessagesContainerTarget) {
      this.messagesContainerTarget.classList.remove('d-none')
    }
  }

  showDrafts(event) {
    if (event) event.preventDefault()
    this.loadFrameSrc('/messages/drafts')
    this.setActiveItem(event.currentTarget, 'activity')
    this.setContext('activity', 'drafts', 'My Drafts', 'drafts')
  }

  showThreads(event) {
    if (event) event.preventDefault()
    this.loadFrameSrc('/messages/threads')
    this.setActiveItem(event.currentTarget, 'activity')
    this.setContext('activity', 'threads', 'My Threads', 'threads')
  }

  /* -- Search and filtering -- */

  search(event) {
    const query = event.target.value.toLowerCase()
    this.filterListItems(query)

    if (event.key === 'Enter') {
      if (query.length >= 2) {
        this.performGlobalSearch(query)
      }
    }
  }

  performGlobalSearch(query) {
    const frame = this.getMessagesFrame()
    if (!frame) return

    const searchPath = `/messages/search?q=${encodeURIComponent(query)}`
    frame.src = searchPath

    const newUrl = `${this.baseUrlValue}/search?q=${encodeURIComponent(query)}`
    window.history.pushState({}, '', newUrl)

    this.setContext('search', 'global', `Search: ${query}`, 'search')
  }

  filterListItems(query) {
    for (const selector of ['.channel-item', '.direct-item', '.entity-item']) {
      for (const item of this.element.querySelectorAll(selector)) {
        const name = item.textContent.toLowerCase()
        if (query === '' || name.includes(query)) {
          item.classList.remove('d-none')
        } else {
          item.classList.add('d-none')
        }
      }
    }
  }

  /* -- Sidebar sections -- */

  toggleSection(event) {
    const sectionId = event.currentTarget.dataset.sectionId
    if (!sectionId) return

    const content = this.sectionContentTargets.find(t => t.dataset.sectionId === sectionId)
    const icon = this.sectionIconTargets.find(t => t.dataset.sectionId === sectionId)

    if (content) {
      content.classList.toggle('show')
      const isVisible = content.classList.contains('show')

      if (icon) {
        icon.classList.toggle('section-chevron-rotated', isVisible)
      }
    }
  }

  /* -- Initial state detection -- */

  updateActiveStates() {
    const params = new URLSearchParams(window.location.search)
    const channelId = params.get('channel')
    const entityType = params.get('entity_type')
    const entityId = params.get('entity_id')

    if (channelId) {
      const button = this.element.querySelector(`[data-channel-id="${channelId}"]`)
      if (button) {
        button.classList.add('active')
        this.setContext('channel', channelId, button.dataset.channelName,
          button.dataset.channelType || 'public_channel')
      } else {
        this.setContext('channel', channelId, 'Channel')
      }
      this.loadMessages('channels', channelId)
    } else if (entityType && entityId) {
      this.loadEntityMessages(entityType, entityId)
    }
  }

  /* -- Helpers -- */

  getMessagesFrame() {
    return document.getElementById(this.messagesFrameValue)
  }
}

/* -- Pure formatting functions (extracted for reusability and testability) -- */

function formatContextName(type, name, subtype) {
  switch (type) {
    case 'channel':
      return subtype === 'private_channel' ? `🔒 ${name}` : `# ${name}`
    case 'direct':
      return name
    case 'entity':
      return `${subtype}: ${name}`
    default:
      return name
  }
}

function formatContextDescription(type, subtype) {
  switch (type) {
    case 'channel':
      return subtype === 'private_channel' ? 'Private channel' : 'Public channel'
    case 'direct':
      return 'Direct message'
    case 'entity':
      return `${subtype} messages`
    default:
      return ''
  }
}

function getContextIcon(type, subtype) {
  let iconClass = 'fa-comments'
  let bgClass = 'bg-primary'

  switch (type) {
    case 'channel':
      iconClass = subtype === 'private_channel' ? 'fa-lock' : 'fa-hashtag'
      bgClass = subtype === 'private_channel' ? 'bg-warning' : 'bg-primary'
      break
    case 'direct':
      iconClass = 'fa-user'
      bgClass = 'bg-success'
      break
    case 'entity':
      iconClass = 'fa-file-alt'
      bgClass = 'bg-secondary'
      break
  }

  return `<div class="${bgClass} rounded-circle-exact avatar-2rem">
            <i class="fas ${iconClass} text-white icon-md"></i>
          </div>`
}
